import {
    ConvenienceStore,
    ECFormCustomField,
    InstallmentPlan,
    OnlineBrand,
    PaymentType,
    TemporaryTokenAliasQrLogoType,
    TransactionTokenType,
    UsageLimit,
} from "univapay-node";
import { CardBrand } from "univapay-node";

import {
    MessageChargeCreated,
    MessageError,
    MessageSubscriptionCreated,
    MessageSuccess,
    MessageThreeDsAuthorization,
    MessageThreeDsAuthorizationFailure,
    MessageThreeDsAuthorizationSuccess,
    MessageTokenCreated,
    MessageValidationError,
} from "./Messages";

/** Patched SDK types */
export enum NewCardBrand {
    PRIVATE_LABEL = "private_label",
}

export type PatchedCardBrand = CardBrand | NewCardBrand;
/** End patched SDK types */

export enum CheckoutType {
    PAYMENT = "payment",
    TOKEN = "token",
    QR = "qr",
}

export enum IFrameType {
    DIALOG = "dialog",
    INLINE = "inline",
}

/**
 * TODO: Use correct type when testcafe updates its own typescript (version >= 2.0.0)
 */
export type PeriodDuration = string; // "D" | "M" | "Y"
export type Period = string; // `P${number}${PeriodDuration}`

export enum InlineSplitElement {
    // Main element in charge of holding the parameters and fetching the data. Automatically set by the client connector
    MAIN = "main",

    SUBMIT = "submit",

    // Customer data
    EMAIL = "email",
    PHONE_NUMBER = "phoneNumber",
    NAME = "name",
    NAME_KANA = "nameKana",
    BILLING_ADDRESS = "billingAddress",
    BILLING_ADDRESS_COUNTRY = "country",
    BILLING_ADDRESS_ZIP = "zip",
    BILLING_ADDRESS_STATE = "state",
    BILLING_ADDRESS_CITY = "city",
    BILLING_ADDRESS_ADDRESS = "address",
    BILLING_ADDRESS_ADDRESS_COMPLEMENT = "addressComplements",

    /**
     * Shortcut to show all customer data
     */
    CUSTOMER_DATA = "customerData",

    // Card data
    DATA_CARD_NUMBER = "cardNumber",
    DATA_CVV = "cvv",
    DATA_EXPIRY = "expiryDate",
    DATA_CARDHOLDER_NAME = "cardHolder",
    DATA_ACCEPT_RECURRING = "recurring",
    DATA_ACCEPT_RECURRING_OPTIONAL = "recurring_optional",
    DATA_CARD_INSTALLMENTS = "cardInstallments",
    DATA_PRIVACY_POLICY = "privacyPolicy",
    /**
     * Shortcut to show all card data
     */
    DATA = "data",
}

export enum ThreeDsMode {
    NORMAL = "normal",
    SKIP = "skip",
    FORCE = "force",
    IF_AVAILABLE = "if_available",
    REQUIRE = "require",
    PROVIDED = "provided",
}

export type CommonParams = {
    tokenType: TransactionTokenType;
    checkout: CheckoutType;

    /**
     * Boolean when the checkout should be displayed as an inline form instead of a dialog
     */
    inline?: boolean;
    inlineStyles?: Record<InlineStyleKeys, Record<string, string>>;
    inlineSplitId?: string;
    inlineSplitElements?: InlineSplitElement | InlineSplitElement[];

    /**
     * Important note: Setting this parameter will use the link form instead of the widget.
     * Do not share this parameter with QC or support until the whole feature is ready for production.
     */
    responsive?: boolean;

    address: boolean;
    amount: number;
    appId: string;
    secret?: string;
    autoClose: boolean;
    autoCloseOnError: boolean;
    autoSubmit: boolean;
    autoSubmitOnError: boolean;
    capture: boolean;
    captureAt: string;
    captureIn: Period;
    captureDayOfMonth?: number;
    confirmationRequired: boolean;
    merchantCardRegistration?: boolean;
    skipReviewStep?: boolean;
    tokenOnly?: boolean;
    currency: string;
    description: string;
    email: string;
    phoneNumber?: string;
    phoneNumberCountryCode?: string;
    customerName?: string;
    customerNameKana?: string;
    locale: string;
    usageLimit: UsageLimit;
    subscriptionId: string;
    subscriptionInitialAmount: number;
    requireEmail?: boolean;
    requireBillingAddress?: boolean;
    requirePhoneNumber?: boolean;
    requireName?: boolean;
    requireNameKana?: boolean;
    subscriptionStart: string;
    subscriptionStartIn: Period;
    installmentPlan?: InstallmentPlan;
    subscriptionPeriod?: string;
    subscriptionPlan?: InstallmentPlan;
    installmentQty?: number;
    subscriptionQty?: number;
    title: string;
    header: string;
    dark: boolean;
    showLogo: boolean;
    hideTestBanner: boolean;
    preventStepNavigation: boolean;
    timeout?: number;
    hideRecurringCheckbox?: boolean;
    hidePrivacyLink?: boolean;
    showCvv: boolean;
    univapayCustomerId: string;
    subscriptionTimezone: string;
    subscriptionPreserveEndOfMonth?: boolean;
    subscriptionRetryInterval?: string;
    threeDsMode: ThreeDsMode;
    threeDsRedirect?: string;
    threeDsSkipToken?: boolean;
    onlineRedirect?: string;

    // User installments
    allowCardInstallments?: boolean;
    cardInstallmentOptions?: string[];

    subscriptionStartDayOfMonth: number;
    products?: string[];
    productCodes?: string[];
    productQuantities?: number[];
    productCodeQuantities?: number[];
    paymentType?: PaymentType | OnlineBrand;
    paymentTypes?: (PaymentType | OnlineBrand | PatchedCardBrand | ConvenienceStore)[];
    paymentMethods?: (PaymentType | OnlineBrand | PatchedCardBrand | ConvenienceStore)[];
    descriptor: string;
    ignoreDescriptorOnError: boolean;
    onlyDirectCurrency: boolean;
    buyerName: string;
    buyerNameTransliteration: string;
    buyerDateOfBirth: string;
    submitButtonText?: string;
    univapayReferenceId?: string;

    bankTransferExpirationPeriod?: string;
    bankTransferExpirationTimeShift?: string;
    convenienceStoreExpirationPeriod?: string;
    convenienceStoreExpirationTimeShift?: string;

    // Legacy expiration params
    expirationPeriod?: string;
    expirationTimeShift?: string;

    shippingAddressLine1: string;
    shippingAddressLine2: string;
    shippingAddressCity: string;
    shippingAddressState: string;
    shippingAddressZip: string;
    shippingAddressCountryCode: string;

    customFields?: ECFormCustomField[];

    onSuccess?: (message: MessageSuccess) => void;
    onPending?: (message: MessageSuccess) => void;
    callback?: (message: MessageSuccess) => void; // legacy parameter for onSuccess
    onError?: (message: MessageError) => void;
    beforeClosing?: () => boolean;
    onTokenCreated?: (message: MessageTokenCreated) => boolean;
    onChargeCreated?: (message: MessageChargeCreated) => boolean;
    onSubscriptionCreated?: (message: MessageSubscriptionCreated) => boolean;
    onThreeDsAuthorization?: (message: MessageThreeDsAuthorization) => boolean;
    onThreeDsAuthorizationSuccess?: (message: MessageThreeDsAuthorizationSuccess) => boolean;
    onThreeDsAuthorizationFailure?: (message: MessageThreeDsAuthorizationFailure) => boolean;
    onThreeDsAuthorizationModalOpened?: () => boolean;
    onValidationError?: (errors: string[] | MessageValidationError) => boolean;
    opened?: () => void;
    closed?: () => void;
    hasBeforeClosingCallback?: boolean;

    /**
     * For recurring token, save the CVV with the card data for future usage
     */
    cvvAuthorize: boolean;

    qrColor: string;
    qrLogoType: TemporaryTokenAliasQrLogoType;

    /**
     * UUID. When set forces an instant charge with the given token id.
     */
    instantChargeTokenId?: string;

    cardholder?: string;
    exp?: string;
    placeholderCardNumberLastFour?: string;
    placeholderCardBrand?: CardBrand;
    callCenterTokenId?: string;
};

type InlineParams = {
    inlineBaseFontSize?: string;
    horizontalInlineLayout?: boolean;
} & CheckoutStyles;

export interface MetadataParams {
    [key: string]: string | boolean | number | BigInt;
}

export type MetaParams = {
    metadata: MetadataParams;
    metadataCharge: MetadataParams;
    metadataSubscription: MetadataParams;
    metadataToken: MetadataParams;
};

export type CustomFieldParams = {
    customFieldKeys?: string;
    customFieldLabels?: string;
    customFieldTypes?: string;
    customFieldRequired?: string;
    customFieldOptions?: string;
};

export type CheckoutStyles = {
    inlineBaseFontSize?: string;
    inlineItemStyle?: string;
    inlineItemLabelStyle?: string;
    inlineItemErrorStyle?: string;
    inlineFieldStyle?: string;
    inlineFieldInvalidStyle?: string;
    inlineFieldFocusStyle?: string;

    inlineTextFieldStyle?: string;
    inlineTextFieldInvalidStyle?: string;
    inlineTextFieldFocusStyle?: string;
    inlineTextItemStyle?: string;
    inlineTextItemLabelStyle?: string;
    inlineTextItemErrorStyle?: string;

    inlineToggleFieldStyle?: string;
    inlineToggleFieldInvalidStyle?: string;
    inlineToggleFieldFocusStyle?: string;
    inlineToggleItemStyle?: string;
    inlineToggleItemLabelStyle?: string;
    inlineToggleItemErrorStyle?: string;

    inlineSelectFieldStyle?: string;
    inlineSelectFieldInvalidStyle?: string;
    inlineSelectFieldFocusStyle?: string;
    inlineSelectItemStyle?: string;
    inlineSelectItemLabelStyle?: string;
    inlineSelectItemErrorStyle?: string;

    inlineRadioItemStyle?: string;
    inlineRadioItemLabelStyle?: string;
    inlineRadioFieldFocusStyle?: string;

    horizontalInlineLayout?: boolean;
};

export enum InlineStyleKeys {
    ITEM = "item",
    ITEM_LABEL = "item-label",
    ITEM_ERROR = "item-error",
    FIELD = "field",
    FIELD_INVALID = "field-invalid",
    FIELD_FOCUS = "field-focus",

    TEXT_FIELD = "text-field",
    TEXT_FIELD_INVALID = "text-field-invalid",
    TEXT_FIELD_FOCUS = "text-field-focus",
    TEXT_ITEM = "text-item",
    TEXT_ITEM_LABEL = "text-item-label",
    TEXT_ITEM_ERROR = "text-item-error",

    TOGGLE_FIELD = "toggle-field",
    TOGGLE_FIELD_INVALID = "toggle-field-invalid",
    TOGGLE_FIELD_FOCUS = "toggle-field-focus",
    TOGGLE_ITEM = "toggle-item",
    TOGGLE_ITEM_LABEL = "toggle-item-label",
    TOGGLE_ITEM_ERROR = "toggle-item-error",

    SELECT_FIELD = "select-field",
    SELECT_FIELD_INVALID = "select-field-invalid",
    SELECT_FIELD_FOCUS = "select-field-focus",
    SELECT_ITEM = "select-item",
    SELECT_ITEM_LABEL = "select-item-label",
    SELECT_ITEM_ERROR = "select-item-error",

    RADIO_ITEM = "radio-item",
    RADIO_ITEM_LABEL = "radio-item-label",
    RADIO_FIELD_FOCUS = "radio-field-focus",
}

export type CheckoutParams = CommonParams &
    MetaParams &
    InlineParams &
    CustomFieldParams & {
        dark?: boolean;
        headerBackgroundColor?: string;
        buttonBackgroundColor?: string;
        subHeaderColor?: string;
        backgroundColor?: string;
    };
